<template>
  <Modal name="VIEW_CONTRACT_MODAL" :title="$t('pages.title.contractDetails')" :width="900" :actions="true">
    <template #body>
      <v-progress-circular v-if="loading" indeterminate color="primary" />
      <v-alert v-else-if="error" type="warning">{{ $t(error) }}</v-alert>
      <v-alert v-else-if="!contract" type="info" class="mb-0">
        {{ $t('contract.realEstateHasNoActiveContract') }}
      </v-alert>
      <div v-else>
        <v-row class="mt-4">
          <v-col cols="12" md="3">
            <h6 class="text-uppercase grey--text">{{ $t('contract.label.contractType') }}</h6>
            <span>{{ $t(`contract.label.subscriptionTypes.${contract.type}`) || '---' }}</span>
          </v-col>
          <v-col cols="12" md="3">
            <h6 class="text-uppercase grey--text">{{ $t('contract.label.startDate') }}</h6>
            <span>{{ formatDate(contract.startDate) || '---' }}</span>
          </v-col>
          <v-col cols="12" md="3">
            <h6 class="text-uppercase grey--text">{{ $t('contract.label.endDate') }}</h6>
            <span>{{ formatDate(contract.endDate) || '---' }}</span>
          </v-col>
          <v-col cols="12" md="3">
            <h6 class="text-uppercase grey--text">{{ $t('contract.label.monthlyCapacity') }}</h6>
            <span>{{ contract.monthlyCapacity || '---' }}</span>
          </v-col>
        </v-row>
        <v-row class="mt-4">
          <v-col cols="12" md="3">
            <h6 class="text-uppercase grey--text">{{ $t('contract.label.monthlyAmount') }}</h6>
            <span>{{ contract.dailyCapacity || '---' }}</span>
          </v-col>
        </v-row>
        <v-divider class="mt-5 mb-8" />
        <h3 class="mb-5">{{ $t('announcement.label.summary.services') }}</h3>
        <div v-for="service in contract.serviceContracts" :key="service.id" class="service-section">
          <h4>{{ $t(`appointment.type.${service.type}`) || '---' }}</h4>
          <v-row class="mt-4">
            <v-col cols="12" md="3">
              <h6 class="text-uppercase grey--text">{{ $t('contract.label.price') }}</h6>
              <span>R$ {{ service.price || '---' }}</span>
            </v-col>
            <v-col cols="12" md="3">
              <h6 class="text-uppercase grey--text">{{ $t('contract.details.squareMetersPrice') }}</h6>
              <span>R$ {{ service.squareMeterPrice || '---' }}</span>
            </v-col>
            <v-col cols="12" md="3">
              <h6 class="text-uppercase grey--text">{{ $t('contract.details.squareMetersTax') }}</h6>
              <span>{{ service.squareMeterTax || '---' }}%</span>
            </v-col>
            <v-col cols="12" md="3">
              <h6 class="text-uppercase grey--text">{{ $t('contract.details.baseSquareMetersPrice') }}</h6>
              <span>{{ service.baseSquareMetersSize || '---' }} m²</span>
            </v-col>
          </v-row>
          <v-row class="mt-4">
            <v-col cols="12" md="3">
              <h6 class="text-uppercase grey--text">{{ $t('contract.details.furnitureTax') }}</h6>
              <span>{{ service.furnitureTax || '---' }}%</span>
            </v-col>
            <v-col cols="12" md="3">
              <h6 class="text-uppercase grey--text">{{ $t('contract.details.semiFurnitureTax') }}</h6>
              <span>{{ service.semiFurnitureTax || '---' }}%</span>
            </v-col>
            <v-col cols="12" md="3">
              <h6 class="text-uppercase grey--text">{{ $t('contract.details.houseTax') }}</h6>
              <span>{{ service.houseTax || '---' }}%</span>
            </v-col>
            <v-col cols="12" md="3">
              <h6 class="text-uppercase grey--text">{{ $t('contract.details.trousseauTax') }}</h6>
              <span>{{ service.trousseauTax || '---' }}%</span>
            </v-col>
          </v-row>
        </div>
      </div>
    </template>
    <template #actions>
      <v-btn color="black" dark text @click="closeModal">{{ $t('dashboard.cardAppointment.btnClose') }}</v-btn>
    </template>
  </Modal>
</template>

<script>
import Modal from '@/components/app/Modal.vue'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'ViewContractModal',
  components: { Modal },
  props: {
    client: Object,
  },
  computed: {
    ...mapGetters([
      'serviceProviderContract',
      'serviceProviderContractError',
      'isServiceProviderLoading',
      'activeModal',
    ]),
    contract() {
      if (!this.serviceProviderContract) return null

      const data = this.serviceProviderContract
      return {
        id: data.id,
        type: data.type,
        startDate: data.start_date || data.startDate,
        endDate: data.end_date || data.endDate,
        monthlyCapacity: data.monthly_capacity || data.monthlyCapacity,
        dailyCapacity: data.daily_capacity || data.dailyCapacity,
        serviceContracts: (data.service_contracts || data.serviceContracts || []).map(service => ({
          id: service.id,
          type: service.type,
          price: service.price,
          squareMeterPrice: service.square_meter_price || service.squareMeterPrice,
          squareMeterTax: service.square_meter_tax || service.squareMeterTax,
          baseSquareMetersSize: service.base_square_meters_size || service.baseSquareMetersSize,
          furnitureTax: service.furniture_tax || service.furnitureTax,
          semiFurnitureTax: service.semi_furniture_tax || service.semiFurnitureTax,
          houseTax: service.house_tax || service.houseTax,
          trousseauTax: service.trousseau_tax || service.trousseauTax,
        })),
      }
    },
    loading() {
      return this.isServiceProviderLoading
    },
    error() {
      if (this.serviceProviderContractError) return this.serviceProviderContractError
      return null
    },
  },
  watch: {
    activeModal(val) {
      if (val === 'VIEW_CONTRACT_MODAL' && this.client?.id) {
        this.fetchContract()
      } else if (val !== 'VIEW_CONTRACT_MODAL') {
        this.clearContractState()
      }
    },
  },
  methods: {
    ...mapActions(['closeModal', 'showServiceProviderContractByRealEstate']),
    async fetchContract() {
      try {
        await this.showServiceProviderContractByRealEstate(this.client.id)
      } catch {
      }
    },
    clearContractState() {
      this.$store.commit('setServiceProviderContract', null)
      this.$store.commit('setServiceProviderContractError', null)
    },
    formatDate(date) {
      if (!date) return '---'
      const d = new Date(date)
      return !isNaN(d) ? d.toLocaleDateString('pt-BR') : '---'
    },
  },
}
</script>

<style scoped>
.service-section {
  margin-bottom: 20px;
}
</style>